import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { GradientButton, IconButton } from '../../components/Buttons';
import BodyWrap from '../../components/BodyWrap';
import { IconCopy, IconCopyCheck, IconKey, IconTrash, IconTrashX } from '@tabler/icons-react';
import { Modal, Select, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { Environment, Project } from '../../types/Project';
import { APIKey } from '../../types/APIKey';
import { ItemSelection } from '../../types/Common';
import { format, parseISO } from 'date-fns';
import { clearLocalStorage } from '../../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { AdminOnlyComponent } from '../../components/ProtectedRoute';

type CreateAPIKeyFormValues = {
  apiKeyName: string;
  projectKey: string;
  environmentKey: string | null;
};

function ApiSettings() {
  useEffect(() => {
    document.title = 'API Key | PromptSlate';
  }, []);
  const navigate = useNavigate();
  const [createAPIKeyModalOpened, setCreateAPIKeyModalOpened] = useState(false);
  const [confirmRevokeModalOpened, setConfirmRevokeModalOpened] = useState(false);
  let [apiKeyCreateMessage, setAPIKeyCreateMessage] = useState<string>('');
  let [projectListMessage, setProjectListMessage] = useState<string>('');
  let [keyRevokeMessage, setKeyRevokeMessage] = useState<string>('');
  let [createdAPIKey, setCreatedAPIKey] = useState<string>('');
  let [isLoading, setIsLoading] = useState<boolean>(false);
  const [copyStatus, setCopyStatus] = useState(false);
  const [projectSelectionList, setProjectSelectionList] = useState<ItemSelection[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedAPIKeyToRevoke, setSelectedAPIKeyToRevoke] = useState<string | null>(null);
  const [selectedAPIKeyUUIDToRevoke, setSelectedAPIKeyUUIDToRevoke] = useState<string | null>(null);
  const [typedAPIKeyToRevoke, setTypedAPIKeyToRevoke] = useState<string | null>(null);
  const [environmentSelectionList, setEnvironmentSelectionList] = useState<ItemSelection[]>([]);

  const handleRevokeAPIKeyModalToggle = (opened: boolean) => {
    setConfirmRevokeModalOpened(opened);
  };

  useEffect(() => {
    if (selectedProject) {
      createAPIKeyForm.setFieldValue('environmentKey', null);
      setEnvironmentSelectionList([]);
      fetchEnvironments(selectedProject);
    }
  }, [selectedProject]);

  const {
    error: keyFetchError,
    data: apiKeys,
    refetch: refetchAPIKeys,
  } = useQuery({
    queryKey: ['apiKeys'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api-key`, {
            headers: headers,
          });
          const apiKeys: APIKey[] = response.data.api_keys;
          return apiKeys;
        } else {
          return [];
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setProjectListMessage('Unable to fetch api keys');
            return [];
          } else {
            setProjectListMessage('A network error occured. Please try again later.');
          }
        } else {
          setProjectListMessage('An error occured. Please try again later.');
          return [];
        }
      }
    },
  });

  const fetchEnvironments = async (projectKey: string) => {
    try {
      const authToken = localStorage.getItem('authToken');

      if (authToken) {
        const headers = {
          Authorization: 'Bearer ' + authToken,
        };
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/project/${projectKey}/environment`,
          {
            headers: headers,
          }
        );
        const environments: Environment[] = response.data.environments;
        const envSelectData = environments.map((environment) => ({
          value: environment.key,
          label: environment.name,
        }));
        setEnvironmentSelectionList(envSelectData);
      } else {
        return [];
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setProjectListMessage('Unable to fetch projects');
          return [];
        } else {
          setProjectListMessage('A network error occured. Please try again later.');
          return [];
        }
      } else {
        setProjectListMessage('An error occured. Please try again later.');
        return [];
      }
    }
  };

  const {
    error,
    data: projectList,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ['projectList'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/project`, {
            headers: headers,
          });
          const projects: Project[] = response.data.projects;
          const projectSelectData = projects.map((project) => ({
            value: project.key,
            label: project.name,
          }));
          setProjectSelectionList(projectSelectData);
          return projects;
        } else {
          return [];
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setProjectListMessage('Unable to fetch projects');
            return [];
          } else {
            setProjectListMessage('A network error occured. Please try again later.');
          }
        } else {
          setProjectListMessage('An error occured. Please try again later.');
          return [];
        }
      }
    },
  });

  const handleCreateAPIKeyModalToggle = (opened: boolean) => {
    setCreatedAPIKey('');
    setCreateAPIKeyModalOpened(opened);
  };

  const createAPIKeyForm = useForm<CreateAPIKeyFormValues>({
    initialValues: {
      apiKeyName: '',
      projectKey: '',
      environmentKey: null,
    },

    validate: {
      apiKeyName: (value: string) => {
        if (!value) {
          return 'Enter a name for API key';
        }
      },
      projectKey: (value: string) => {
        if (!value) {
          return 'Select a project';
        }
      },
      environmentKey: (value: string | null) => {
        if (!value) {
          return 'Select an environment';
        }
      },
    },
  });

  const handleCreateAPIKeySubmit = async (values: any) => {
    setAPIKeyCreateMessage('');
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api-key/create`,
        {
          name: values.apiKeyName,
          project_key: values.projectKey,
          environment_key: values.environmentKey,
        },
        {
          headers: headers,
        }
      );
      setCreatedAPIKey(response.data.key);
      refetchAPIKeys();
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setAPIKeyCreateMessage('Invalid request');
        } else {
          setAPIKeyCreateMessage('A network error occured. Please try again later.');
        }
      } else {
        setAPIKeyCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  const handleRevokeAPIKey = async () => {
    setIsLoading(true);

    if (selectedAPIKeyToRevoke !== typedAPIKeyToRevoke) {
      setKeyRevokeMessage('The name you typed do not match the key you selected for deletion');
      setIsLoading(false);
      return;
    }
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api-key/revoke`,
        {
          uuid: selectedAPIKeyUUIDToRevoke,
        },
        {
          headers: headers,
        }
      );
      refetchAPIKeys();
      setConfirmRevokeModalOpened(false);
      setSelectedAPIKeyToRevoke('');
      setSelectedAPIKeyUUIDToRevoke('');
      setAPIKeyCreateMessage('');
      setKeyRevokeMessage('');
      setTypedAPIKeyToRevoke('');
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setAPIKeyCreateMessage('Invalid request');
        } else {
          setAPIKeyCreateMessage('A network error occured. Please try again later.');
        }
      } else {
        setAPIKeyCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  const handleAPIKeyCopy = async () => {
    try {
      await navigator.clipboard.writeText(createdAPIKey);
      setCopyStatus(true);
      setTimeout(() => setCopyStatus(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <BodyWrap>
      <Modal
        opened={createAPIKeyModalOpened}
        onClose={() => handleCreateAPIKeyModalToggle(false)}
        title={<Text size={'xl'}>Create an API key</Text>}
        size={'lg'}
        radius="lg"
      >
        {createdAPIKey.length == 0 ? (
          <form
            noValidate
            onSubmit={createAPIKeyForm.onSubmit((values) => handleCreateAPIKeySubmit(values))}
            className="flex flex-col gap-5 pl-5 pr-5"
          >
            {projectList ? (
              <div>
                {' '}
                <Select
                  placeholder="Select a project"
                  data={projectSelectionList}
                  value={selectedProject}
                  onChange={(value) => {
                    if (value) {
                      setSelectedProject(value);
                      createAPIKeyForm.setFieldValue('projectKey', value);
                    }
                  }}
                  allowDeselect={false}
                  className="w-full mt-5 text-lg"
                  styles={{
                    input: {
                      height: 45,
                      paddingTop: 6,
                      paddingBottom: 6,
                    },
                  }}
                />
                <Select
                  placeholder="Select an environment"
                  data={environmentSelectionList}
                  value={createAPIKeyForm.values.environmentKey}
                  onChange={(value) => {
                    if (value) {
                      createAPIKeyForm.setFieldValue('environmentKey', value);
                    }
                  }}
                  allowDeselect={false}
                  className="w-full mt-5 text-lg"
                  styles={{
                    input: {
                      height: 45,
                      paddingTop: 6,
                      paddingBottom: 6,
                    },
                  }}
                />
                <TextInput
                  className="mt-5"
                  size="md"
                  placeholder="Name of API key"
                  {...createAPIKeyForm.getInputProps('apiKeyName')}
                />
                <GradientButton className="mt-8 mb-5" type="submit">
                  Create API Key
                </GradientButton>
              </div>
            ) : (
              <div />
            )}
          </form>
        ) : (
          <div>
            <span>
              Save your secret key securely — <b>you won’t be able to view it again</b>. Anyone with
              it can access your API. If lost, you must generate a new one.
            </span>
            <div className="flex flex-row mt-8 mb-5">
              <div className=" w-3/4 border border-gray-200 overflow-scroll h-[45px] flex items-center p-2 pr-2 rounded-md scrollbar-hide">
                {createdAPIKey}
              </div>
              {copyStatus ? (
                <IconButton
                  title="Create a new API key"
                  onClick={handleAPIKeyCopy}
                  className="w-1/4 ml-2 h-[45px]"
                >
                  <IconCopyCheck size="1.5rem" className="mr-2" /> Copy key
                </IconButton>
              ) : (
                <IconButton
                  title="Create a new API key"
                  onClick={handleAPIKeyCopy}
                  className="w-1/4 ml-2 h-[45px]"
                >
                  <IconCopy size="1.5rem" className="mr-2" /> Copy key
                </IconButton>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        opened={confirmRevokeModalOpened}
        onClose={() => handleRevokeAPIKeyModalToggle(false)}
        title={<Text size={'xl'}>Revoke API Key</Text>}
        size={'lg'}
        radius="lg"
      >
        <span>
          The API '<b>{selectedAPIKeyToRevoke}</b>' will be immediately disabled.{' '}
          <b>All requests using it will fail, potentially breaking dependent systems</b>. Once
          revoked, it can't be viewed or modified.
        </span>
        <TextInput
          className="mt-5"
          size="md"
          placeholder="Confirm the name of the API key"
          onChange={(e) => setTypedAPIKeyToRevoke(e.target.value)}
        />
        <span className="mt-2">{keyRevokeMessage}</span>
        <IconButton
          className="mt-5 mb-5 text-red-700  w-full"
          type="submit"
          onClick={() => handleRevokeAPIKey()}
        >
          <IconTrash size="1.2rem" className="mr-2" />
          Revoke API key
        </IconButton>
      </Modal>
      <div className="text-md pb-5 ">API Keys</div>
      <IconButton title="Create a new API key" onClick={() => handleCreateAPIKeyModalToggle(true)}>
        <IconKey size="1.5rem" className="mr-2" /> Create API key
      </IconButton>
      <div className="flex flex-col">
        {apiKeys ? (
          apiKeys.map((apiKey) => {
            const dateCreated = parseISO(apiKey.created_at);
            return (
              <div
                key={apiKey.uuid}
                className="flex w-full mt-5 mr-5 p-5 bg-[#FFFFFF] border border rounded-md"
              >
                <div className="flex-auto w-2/12">{apiKey.name}</div>
                <div className="flex-auto w-3/12">
                  <span className="">
                    {apiKey.project} - {apiKey.environment}
                  </span>
                </div>
                <div className="flex-auto w-3/12 text-[#f28137]">
                  <span className="w-fit bg-[#f4f4f4] pl-2 pr-2 pt-1 pb-1 rounded-md">
                    {apiKey.preview}
                  </span>
                </div>
                <div className="flex-auto w-3/12">
                  <span className="">{apiKey.created_by}</span>
                </div>
                <div className="flex-auto w-3/12">
                  <span className="">{format(dateCreated, 'PPpp')}</span>
                </div>
                <AdminOnlyComponent>
                  <div className="flex-auto w-1/12 text-red-500 hover:text-red-600">
                    <IconTrashX
                      size="1.5rem"
                      className="float-right cursor-pointer"
                      onClick={() => {
                        setSelectedAPIKeyUUIDToRevoke(apiKey.uuid);
                        setSelectedAPIKeyToRevoke(apiKey.name);
                        handleRevokeAPIKeyModalToggle(true);
                      }}
                    />
                  </div>
                </AdminOnlyComponent>
              </div>
            );
          })
        ) : (
          <div className="w-full flex justify-center text-slate-600 ">
            API keys will appear here once created
          </div>
        )}
      </div>
    </BodyWrap>
  );
}

export default ApiSettings;
